import { useState, useMemo, useEffect, useRef } from 'react'
import { useQuery, useMutation } from '@tanstack/react-query'
import { questionnaireApi } from '../api/questionnaire'
import { submissionsApi } from '../api/submissions'
import { aiApi } from '../api/ai'
import QuestionnaireForm from '../components/QuestionnaireForm'
import { AiAssistantPanel } from '../components/AiAssistantPanel'
import Button from '../components/Button'
import Modal from '../components/Modal'
import type { SubmitResult, Question, DuplicateMatch } from '../types'
import { FileText, Download, AlertCircle, CheckCircle2, Copy, Check, KeyRound, Flag } from 'lucide-react'

const SESSION_KEY = 'ev_checklist_session'

type ChecklistSession = {
  code: string
  submissionId: string | null
  fileId: string | null
  responses: Record<string, string | string[]>
  activePage: string | null
}

function loadSession(): Partial<ChecklistSession> {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    return raw ? (JSON.parse(raw) as Partial<ChecklistSession>) : {}
  } catch {
    return {}
  }
}

function normalizeResponseValue(
  value: string,
  inputId: string,
  questions: Question[]
): string | string[] {
  const q = questions.find((x) => x.input_id === inputId)
  if (q?.input_type === 'check') {
    return value
      .split(';')
      .map((s) => s.trim())
      .filter(Boolean)
  }
  return value
}

function responsesToRecord(
  responses: { question_name: string; value: string }[],
  questions: Question[]
): Record<string, string | string[]> {
  const record: Record<string, string | string[]> = {}
  for (const item of responses) {
    const existing = record[item.question_name]
    if (existing === undefined) {
      record[item.question_name] = normalizeResponseValue(item.value, item.question_name, questions)
    } else if (Array.isArray(existing)) {
      const val = typeof item.value === 'string' ? item.value.trim() : item.value
      if (!existing.includes(val)) existing.push(val)
    } else if (existing !== item.value) {
      record[item.question_name] = [existing, item.value]
    }
  }
  return record
}

export default function ChecklistPage() {
  const initial = useRef(loadSession()).current
  const [code, setCode] = useState(initial.code ?? '')
  const [submissionId, setSubmissionId] = useState<string | null>(initial.submissionId ?? null)
  const [fileId, setFileId] = useState<string | null>(initial.fileId ?? null)
  const [responses, setResponses] = useState<Record<string, string | string[]>>(initial.responses ?? {})
  const [submitResult, setSubmitResult] = useState<SubmitResult | null>(null)
  const [activePage, setActivePage] = useState<string | null>(initial.activePage ?? null)
  const [duplicateMatches, setDuplicateMatches] = useState<DuplicateMatch[]>([])
  const duplicateTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  // Tracks the submission id we've already seeded from the server, so refetches
  // (after submit) don't clobber the user's in-progress / restored answers.
  const seededSubmissionId = useRef<string | null>(initial.submissionId ?? null)
  const [loadHint, setLoadHint] = useState<string | null>(null)
  const [showGenerateDialog, setShowGenerateDialog] = useState(false)
  const [copied, setCopied] = useState(false)
  // AI feedback / flagging
  const [aiFilledFields, setAiFilledFields] = useState<Set<string>>(new Set())
  const [flaggedFields, setFlaggedFields] = useState<Set<string>>(new Set())
  const [flagQ, setFlagQ] = useState<Question | null>(null)
  const [flagValue, setFlagValue] = useState('')
  const [flagType, setFlagType] = useState<'wrong' | 'incomplete' | 'missing'>('wrong')
  const [flagCorrect, setFlagCorrect] = useState('')
  const [flagComment, setFlagComment] = useState('')
  const [flagError, setFlagError] = useState<string | null>(null)
  const [flagSubmitting, setFlagSubmitting] = useState(false)

  const { data: questions, isLoading: questionsLoading } = useQuery({
    queryKey: ['questionnaire'],
    queryFn: questionnaireApi.get,
  })

  const { data: submission, refetch: refetchSubmission } = useQuery({
    queryKey: ['submission', submissionId],
    queryFn: () => (submissionId ? submissionsApi.get(submissionId) : null),
    enabled: !!submissionId,
  })

  useEffect(() => {
    if (!submission) return
    setFileId(submission.file_id)
    // Seed answers from the server only the first time we see this submission
    // (initial load of a code). On later refetches — or when we've restored an
    // in-progress session from localStorage — keep the local answers so unsaved
    // edits aren't lost.
    if (questions && seededSubmissionId.current !== submission.id) {
      seededSubmissionId.current = submission.id
      setResponses((prev) =>
        Object.keys(prev).length > 0 ? prev : responsesToRecord(submission.responses, questions)
      )
    }
  }, [submission, questions])

  // Persist the working session so navigating away and back keeps the checklist.
  useEffect(() => {
    try {
      localStorage.setItem(
        SESSION_KEY,
        JSON.stringify({ code, submissionId, fileId, responses, activePage })
      )
    } catch {
      // Storage may be unavailable (private mode / quota) — ignore.
    }
  }, [code, submissionId, fileId, responses, activePage])

  const pages = useMemo(() => {
    if (!questions) return []
    return Array.from(new Set(questions.map((q) => q.page)))
  }, [questions])

  useEffect(() => {
    if (pages.length > 0 && !activePage) {
      setActivePage(pages[0])
    }
  }, [pages, activePage])

  // Debounced duplicate check when Title or PMID/DOI changes.
  useEffect(() => {
    if (duplicateTimerRef.current) clearTimeout(duplicateTimerRef.current)

    const titleRaw = responses['Title']
    const title = Array.isArray(titleRaw) ? titleRaw.join(' ') : titleRaw || ''
    const pmidRaw = responses['PMID']
    const pmid = (Array.isArray(pmidRaw) ? pmidRaw.join(' ') : pmidRaw || '').trim()

    let pmc_id = ''
    let doi = ''
    if (/^PMC\d+/i.test(pmid)) {
      pmc_id = pmid.toUpperCase()
    } else if (/^10\.\d+/.test(pmid)) {
      doi = pmid
    }

    const hasSearch = title.trim().length > 5 || pmc_id || doi
    if (!hasSearch) {
      setDuplicateMatches([])
      return
    }

    duplicateTimerRef.current = setTimeout(async () => {
      try {
        const res = await submissionsApi.checkDuplicate({ title: title.trim(), pmc_id, doi })
        setDuplicateMatches(res.matches)
      } catch {
        // Fail silently — duplicate check is advisory only.
      }
    }, 800)

    return () => {
      if (duplicateTimerRef.current) clearTimeout(duplicateTimerRef.current)
    }
  }, [responses])

  const validateMutation = useMutation({
    mutationFn: submissionsApi.validateCode,
    onSuccess: (data) => {
      if (data.valid && data.submission_id) {
        // Loading a code: clear local answers and reset the seed guard so the
        // server's saved answers populate the form.
        seededSubmissionId.current = null
        setResponses({})
        setSubmissionId(data.submission_id)
        setFileId(data.file_id || null)
        setSubmitResult(null)
      } else {
        setSubmissionId(null)
        setFileId(null)
        setResponses({})
      }
    },
  })

  const generateMutation = useMutation({
    mutationFn: submissionsApi.generateCode,
    onSuccess: (data) => {
      setCode(data.code)
      validateMutation.mutate(data.code)
    },
  })

  const saveMutation = useMutation({
    mutationFn: ({ id, responses }: { id: string; responses: Record<string, string | string[]> }) =>
      submissionsApi.save(id, responses),
    onSuccess: () => setLastSaved(new Date()),
  })

  const submitMutation = useMutation({
    mutationFn: ({ id, responses }: { id: string; responses: Record<string, string | string[]> }) =>
      submissionsApi.submit(id, responses),
    onSuccess: (data) => {
      setSubmitResult(data)
      if (data.success) {
        refetchSubmission()
      }
    },
  })

  const handleChange = (inputId: string, value: string | string[]) => {
    setResponses((prev) => ({ ...prev, [inputId]: value }))
  }

  const handleBlur = () => {
    if (!submissionId) return
    // Auto-save on blur unless recently saved
    saveMutation.mutate({ id: submissionId, responses })
  }

  const handleSave = () => {
    if (!submissionId) return
    saveMutation.mutate({ id: submissionId, responses })
  }

  const handleSubmit = () => {
    if (!submissionId) return
    setSubmitResult(null)
    submitMutation.mutate({ id: submissionId, responses })
  }

  const handleLoad = () => {
    if (!code.trim()) {
      setLoadHint('Please enter your code first — or click “Generate new code” to start a new checklist.')
      return
    }
    setLoadHint(null)
    validateMutation.mutate(code.trim())
  }

  // Has the user started a checklist that could be lost by switching codes?
  const hasWorkInProgress = !!submissionId

  const handleGenerateClick = () => {
    if (hasWorkInProgress) {
      setShowGenerateDialog(true)
    } else {
      generateMutation.mutate()
    }
  }

  const proceedToGenerate = () => {
    setShowGenerateDialog(false)
    setResponses({})
    setSubmitResult(null)
    generateMutation.mutate()
  }

  const handleSaveAndGenerate = async () => {
    if (submissionId) {
      await saveMutation.mutateAsync({ id: submissionId, responses })
    }
    proceedToGenerate()
  }

  const copyCode = async (value: string) => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard API may be unavailable (e.g. non-HTTPS); ignore silently.
    }
  }

  const openFlag = (q: Question, value: string) => {
    setFlagQ(q)
    setFlagValue(value)
    setFlagType('wrong')
    setFlagCorrect('')
    setFlagComment('')
    setFlagError(null)
  }

  const submitFlag = async () => {
    if (!flagQ || !submissionId) return
    setFlagSubmitting(true)
    setFlagError(null)
    try {
      await aiApi.feedback({
        submission_id: submissionId,
        question_name: flagQ.input_id,
        question_label: flagQ.question,
        original_value: flagValue,
        corrected_value: flagCorrect,
        feedback_type: flagType,
        comment: flagComment,
        was_ai_filled: aiFilledFields.has(flagQ.input_id),
      })
      setFlaggedFields((prev) => new Set(prev).add(flagQ.input_id))
      setFlagQ(null)
    } catch (e: any) {
      setFlagError(e?.response?.data?.detail || e.message || 'Failed to submit feedback.')
    } finally {
      setFlagSubmitting(false)
    }
  }

  const missingRequired = submitResult?.success === false ? submitResult.missing_required : []

  const pagesWithErrors = useMemo(() => {
    if (!questions || missingRequired.length === 0) return new Set<string>()
    const set = new Set<string>()
    for (const inputId of missingRequired) {
      const q = questions.find((x) => x.input_id === inputId)
      if (q) set.add(q.page)
    }
    return set
  }, [questions, missingRequired])

  const pageQuestions = activePage && questions ? questions.filter((q) => q.page === activePage) : []

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 items-start">
        {/* LEFT: submit/modify box + AI assistant — stays visible while the checklist scrolls */}
        <div className="lg:col-span-1 lg:sticky lg:top-20 lg:max-h-[calc(100vh-6rem)] lg:overflow-y-auto space-y-6 pr-1">
          <div className="bg-white rounded-xl border shadow-sm p-6">
            <h2 className="text-2xl font-bold mb-4">Submit or modify checklist</h2>
            <div className="flex flex-col gap-3">
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => {
                      setCode(e.target.value.toUpperCase())
                      if (loadHint) setLoadHint(null)
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleLoad()
                    }}
                    placeholder="Enter code"
                    className="border rounded-lg pl-4 pr-9 py-2 uppercase w-full focus:outline-none focus:ring-2 focus:ring-brand-500"
                    maxLength={5}
                    disabled={validateMutation.isPending}
                  />
                  {code.trim() && (
                    <button
                      type="button"
                      onClick={() => copyCode(code.trim())}
                      title="Copy code"
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-brand-600"
                    >
                      {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                    </button>
                  )}
                </div>
                <Button onClick={handleLoad} isLoading={validateMutation.isPending}>
                  Load
                </Button>
              </div>
              <Button
                variant="secondary"
                onClick={handleGenerateClick}
                isLoading={generateMutation.isPending}
                className="w-full"
              >
                Generate new code
              </Button>
            </div>
        {loadHint && (
          <p className="mt-2 text-sm text-amber-700 flex items-center gap-1.5">
            <AlertCircle className="w-4 h-4" />
            {loadHint}
          </p>
        )}
        {validateMutation.isError && (
          <p className="mt-2 text-sm text-red-600">Invalid or expired code.</p>
        )}
        {duplicateMatches.length > 0 && (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
            <p className="font-semibold text-amber-800 flex items-center gap-1.5 mb-1.5">
              <AlertCircle className="w-4 h-4" />
              {duplicateMatches.length === 1
                ? 'A published checklist with this identifier already exists.'
                : `${duplicateMatches.length} published checklists with this identifier already exist.`}
            </p>
            <ul className="space-y-1">
              {duplicateMatches.map((m) => (
                <li key={m.submission_id} className="text-amber-900">
                  <span className="font-medium">{m.title || `File ${m.file_id}`}</span>
                  {m.pmc_id && <span className="text-amber-700 ml-1">· {m.pmc_id}</span>}
                  <a
                    href={`/reports/${m.file_id}/html`}
                    target="_blank"
                    rel="noreferrer"
                    className="ml-2 underline hover:text-amber-600"
                  >
                    View {m.file_id}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
        {fileId && (
          <div className="mt-3 flex items-center gap-3 text-sm text-gray-600">
            <span className="inline-flex items-center gap-1 bg-brand-50 text-brand-700 px-2.5 py-1 rounded-lg font-medium">
              <FileText className="w-4 h-4" />
              File ID: {fileId}
            </span>
            {submission?.status === 'submitted' && (
              <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 px-2.5 py-1 rounded-lg font-medium">
                <CheckCircle2 className="w-4 h-4" />
                Submitted
              </span>
            )}
          </div>
        )}
          </div>

          {questions && submissionId && (
            <AiAssistantPanel
              submissionId={submissionId}
              onApply={(applied) => {
                const merged: Record<string, string | string[]> = { ...responses }
                for (const [key, raw] of Object.entries(applied)) {
                  const q = questions.find((x) => x.input_id === key)
                  if (q?.input_type === 'check' && typeof raw === 'string') {
                    merged[key] = raw
                      .split(';')
                      .map((s) => s.trim())
                      .filter(Boolean)
                  } else {
                    merged[key] = raw
                  }
                }
                setResponses(merged)
                setAiFilledFields((prev) => {
                  const next = new Set(prev)
                  Object.keys(applied).forEach((k) => next.add(k))
                  return next
                })
                if (submissionId) saveMutation.mutate({ id: submissionId, responses: merged })
              }}
            />
          )}
        </div>

        {/* RIGHT: the fillable checklist */}
        <div className="lg:col-span-2 min-w-0">
          {questionsLoading && (
            <div className="bg-white rounded-xl border shadow-sm p-12 text-center text-gray-500">
              Loading questionnaire…
            </div>
          )}

          {!questionsLoading && !submissionId && (
            <div className="bg-white rounded-xl border shadow-sm p-12 text-center text-gray-500">
              Enter your code and press <span className="font-medium text-gray-700">Load</span> — or click{' '}
              <span className="font-medium text-gray-700">Generate new code</span> — to begin filling the checklist.
            </div>
          )}

          {questions && submissionId && (
            <div className="bg-white rounded-xl border shadow-sm p-6">
            <div className="flex flex-wrap gap-2 border-b mb-6 sticky top-16 bg-white z-10 -mt-6 pt-6">
              {pages.map((page) => {
                const hasError = pagesWithErrors.has(page)
                const isActive = activePage === page
                return (
                  <button
                    key={page}
                    onClick={() => setActivePage(page)}
                    className={`px-4 py-2 font-medium whitespace-nowrap border-b-2 transition-colors flex items-center gap-2 ${
                      isActive
                        ? 'text-brand-700 border-brand-700'
                        : 'text-gray-500 border-transparent hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {page}
                    {hasError && <AlertCircle className="w-4 h-4 text-red-500" />}
                  </button>
                )
              })}
            </div>

            <QuestionnaireForm
              questions={pageQuestions}
              responses={responses}
              onChange={handleChange}
              onBlur={handleBlur}
              missingRequired={missingRequired}
              onFlag={openFlag}
              flaggedFields={flaggedFields}
            />

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mt-8 pt-6 border-t">
              <Button
                variant="secondary"
                onClick={handleSave}
                isLoading={saveMutation.isPending}
              >
                Save draft
              </Button>
              <Button
                onClick={handleSubmit}
                isLoading={submitMutation.isPending}
              >
                {submission?.status === 'submitted' ? 'Update & resubmit' : 'Submit'}
              </Button>
              {lastSaved && (
                <span className="text-sm text-gray-500">
                  Last saved: {lastSaved.toLocaleTimeString()}
                </span>
              )}
            </div>

            {submitResult && (
              <div
                className={`mt-6 p-4 rounded-lg ${
                  submitResult.success ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                }`}
              >
                {submitResult.success ? (
                  <div className="space-y-3">
                    <div className="flex items-center gap-2 font-semibold">
                      <CheckCircle2 className="w-5 h-5" />
                      Submitted successfully! File ID: {submitResult.file_id}
                    </div>
                    <div className="flex flex-wrap gap-3">
                      {submitResult.report_html_url && (
                        <a
                          href={submitResult.report_html_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 bg-white text-brand-700 px-4 py-2 rounded-lg border border-green-200 hover:bg-green-100 transition-colors"
                        >
                          <FileText className="w-4 h-4" />
                          Download HTML
                        </a>
                      )}
                      {submitResult.report_pdf_url && (
                        <a
                          href={submitResult.report_pdf_url}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-2 bg-white text-brand-700 px-4 py-2 rounded-lg border border-green-200 hover:bg-green-100 transition-colors"
                        >
                          <Download className="w-4 h-4" />
                          Download PDF
                        </a>
                      )}
                    </div>
                    {submitResult.cross_platform_links && submitResult.cross_platform_links.length > 0 && (
                      <div className="mt-2 rounded-lg border border-brand-200 bg-brand-50 p-4 text-gray-700">
                        <p className="font-semibold text-brand-800">Related frameworks / resources</p>
                        <ul className="list-disc ml-5 mt-2 space-y-1 text-sm [&_a]:text-brand-700 [&_a]:underline">
                          {submitResult.cross_platform_links.map((link, i) => (
                            <li key={i} dangerouslySetInnerHTML={{ __html: link }} />
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <p className="font-semibold flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Submission incomplete
                    </p>
                    <p className="mt-1 text-sm">
                      Please fill in the missing required fields ({missingRequired.length}):
                    </p>
                    <ul className="list-disc ml-5 mt-2 text-sm space-y-1">
                      {missingRequired.map((f) => {
                        const q = questions.find((x) => x.input_id === f)
                        return <li key={f}>{q ? q.question : f}</li>
                      })}
                    </ul>
                  </div>
                )}
              </div>
            )}
            </div>
          )}
        </div>
      </div>

      <Modal
        open={showGenerateDialog}
        onClose={() => setShowGenerateDialog(false)}
        title="Start a new checklist?"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowGenerateDialog(false)}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={proceedToGenerate} isLoading={generateMutation.isPending}>
              Generate without saving
            </Button>
            <Button onClick={handleSaveAndGenerate} isLoading={saveMutation.isPending || generateMutation.isPending}>
              Save & generate new code
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <p className="text-sm text-gray-700">
            You're currently working on a checklist. Generating a new code starts a fresh, empty checklist.
            Make sure you've <strong>saved your work</strong> and <strong>noted your current code</strong> so
            you can come back to this checklist later.
          </p>
          <div className="rounded-lg border border-brand-200 bg-brand-50 p-4">
            <p className="text-xs font-medium uppercase tracking-wide text-brand-700 mb-1 flex items-center gap-1.5">
              <KeyRound className="w-4 h-4" />
              Your current code
            </p>
            <div className="flex items-center gap-3">
              <span className="font-mono text-2xl font-bold tracking-widest text-gray-900">{code || '—'}</span>
              {code && (
                <button
                  type="button"
                  onClick={() => copyCode(code)}
                  className="inline-flex items-center gap-1 rounded-md border border-brand-300 bg-white px-2.5 py-1.5 text-sm text-brand-700 hover:bg-brand-100"
                >
                  {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              )}
            </div>
            {fileId && (
              <p className="mt-2 text-xs text-gray-500">
                File ID: <span className="font-mono">{fileId}</span>
              </p>
            )}
          </div>
          <p className="text-xs text-gray-500">
            Tip: “Save &amp; generate new code” saves this checklist before creating the new one.
          </p>
        </div>
      </Modal>

      {/* Flag an AI extraction issue for a specific field */}
      <Modal
        open={!!flagQ}
        onClose={() => setFlagQ(null)}
        title={
          <span className="flex items-center gap-2">
            <Flag className="w-5 h-5 text-orange-600" />
            Report an AI extraction issue
          </span>
        }
        footer={
          <>
            <Button variant="ghost" onClick={() => setFlagQ(null)}>
              Cancel
            </Button>
            <Button onClick={submitFlag} isLoading={flagSubmitting}>
              Submit feedback
            </Button>
          </>
        }
      >
        {flagQ && (
          <div className="space-y-4">
            <div>
              <p className="text-sm">
                <span className="font-semibold">Question:</span> {flagQ.question}
              </p>
              <p className="mt-1 text-sm text-gray-600">
                <span className="font-semibold">Current answer:</span>{' '}
                {flagValue ? (
                  <span className="font-mono">{flagValue}</span>
                ) : (
                  <span className="text-gray-400">(empty — AI did not extract this)</span>
                )}
              </p>
              {aiFilledFields.has(flagQ.input_id) && (
                <p className="mt-1 text-xs text-brand-700">This answer was filled by the AI assistant.</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">What is the issue?</label>
              <div className="space-y-1.5">
                {[
                  ['wrong', 'Wrong answer (AI extracted incorrect information)'],
                  ['incomplete', 'Incomplete answer (partially correct but missing details)'],
                  ['missing', 'Missing answer (AI should have extracted this but didn’t)'],
                ].map(([val, lbl]) => (
                  <label key={val} className="flex items-start gap-2 text-sm cursor-pointer">
                    <input
                      type="radio"
                      name="flag-type"
                      value={val}
                      checked={flagType === val}
                      onChange={() => setFlagType(val as 'wrong' | 'incomplete' | 'missing')}
                      className="mt-1 h-4 w-4 text-brand-600 focus:ring-brand-500"
                    />
                    <span className="text-gray-700">{lbl}</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Correct / expected answer</label>
              <input
                type="text"
                value={flagCorrect}
                onChange={(e) => setFlagCorrect(e.target.value)}
                placeholder="Type the correct answer here…"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Additional comment (optional)</label>
              <textarea
                value={flagComment}
                onChange={(e) => setFlagComment(e.target.value)}
                rows={2}
                placeholder="e.g., This value is found in Table 2 of the paper…"
                className="w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
              />
            </div>

            {flagError && <p className="text-sm text-red-600">{flagError}</p>}
          </div>
        )}
      </Modal>
    </div>
  )
}
